import { maskSecrets } from '../../utils/secrets';
import React from 'react';
import { useI18n } from '../../contexts/I18nContext';

interface EffectiveConfigPanelProps {
  effectiveConfig?: Record<string, unknown>;
  /** Keys of effectiveConfig whose value comes from the device's override. */
  overriddenKeys?: string[];
  /** Keys the device's template sets; anything else not overridden comes from the product. */
  templateKeys?: string[];
  templateName?: string | null;
  productName?: string | null;
  lastRequestAt?: string | null;
  lastRequestBy?: string | null;
  /** Show passwords and secrets as they are (ANEP staff); masked otherwise. */
  revealSecrets?: boolean;
}

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value.length > 60 ? value.slice(0, 57) + '…' : value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    const s = JSON.stringify(value);
    return s.length > 60 ? s.slice(0, 57) + '…' : s;
  } catch {
    return String(value);
  }
};

/** The value as shown: masked wherever a secret sits, unless the reader may see them. */
const shownValue = (key: string, value: unknown, reveal: boolean): string =>
  formatValue(reveal ? value : maskSecrets(value, key));

/**
 * The configuration the gateway receives, one row per key, each tagged
 * with the layer that decided it: the product, the device's template or
 * the device's own override (schema v3.1: three layers, resolved key by key).
 */
type Layer = 'product' | 'template' | 'device_override';
const EffectiveConfigPanel: React.FC<EffectiveConfigPanelProps> = ({
  effectiveConfig,
  overriddenKeys,
  templateKeys,
  templateName,
  productName,
  lastRequestAt,
  lastRequestBy,
  revealSecrets = false,
}) => {
  const { t, formatRelativeTime } = useI18n();

  const rows = React.useMemo(
    () => Object.entries(effectiveConfig || {}).filter(([k]) => k !== 'custom_fields').sort(([a], [b]) => a.localeCompare(b)),
    [effectiveConfig],
  );
  const overridden = React.useMemo(() => new Set(overriddenKeys || []), [overriddenKeys]);
  const fromTemplate = React.useMemo(() => new Set(templateKeys || []), [templateKeys]);
  const layerOf = (key: string): Layer => (overridden.has(key) ? 'device_override' : fromTemplate.has(key) ? 'template' : 'product');
  const layerLabel: Record<Layer, string> = {
    product: t('configSources.layers.product'),
    template: t('configSources.layers.template'),
    device_override: t('configSources.layers.deviceOverride'),
  };
  const sourceName: Record<Layer, string | null | undefined> = { product: productName, template: templateName, device_override: null };

  if (rows.length === 0 && !lastRequestAt) return null;

  return (
    <div className="config-sources-panel" data-testid="effective-config-panel">
      <h3>{t('configSources.title')}</h3>
      <p className="config-sources-description">{t('configSources.description')}</p>

      {(lastRequestAt || lastRequestBy) && (
        <div className="config-sources-lastseen">
          <div>
            <strong>{t('configSources.lastSeen')}:</strong>{' '}
            {lastRequestAt ? formatRelativeTime(lastRequestAt) : t('configSources.never')}
          </div>
          <div>
            <strong>{t('configSources.lastSeenBy')}:</strong> {lastRequestBy || '—'}
          </div>
        </div>
      )}

      {rows.length > 0 ? (
        <div className="config-sources-table-wrapper">
          <table className="config-sources-table">
            <thead>
              <tr>
                <th>{t('configSources.field')}</th>
                <th>{t('configSources.value')}</th>
                <th>{t('configSources.source')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(([key, value]) => {
                const layer = layerOf(key);
                return (
                  <tr key={key}>
                    <td className="col-field">{key}</td>
                    <td className="col-value">{shownValue(key, value, revealSecrets)}</td>
                    <td>
                      <span className={`config-sources-layer ${layer}`}>{layerLabel[layer]}</span>
                      {sourceName[layer] && <span className="col-source"> {sourceName[layer]}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="config-sources-empty">{t('configSources.noData')}</div>
      )}
    </div>
  );
};

export default EffectiveConfigPanel;
